<template>
  <div class="rounded-[12px] border border-[#ebedef] bg-white p-[15px]">
    <div class="mb-5 text-[16px] font-medium leading-[20px] text-gray-800">
      Payment details
    </div>

    <div
      v-if="paymentMethod?.last4"
      class="flex flex-col items-start gap-y-2.5 min-[450px]:flex-row min-[450px]:items-center min-[450px]:justify-between min-[450px]:gap-x-4 min-[450px]:gap-y-0"
    >
      <div class="text-[14px] leading-[22px] text-gray-800">Payment method</div>
      <div
        class="flex w-full items-center justify-between gap-x-5 min-[450px]:w-auto min-[450px]:justify-start"
        :class="cardClasses"
      >
        <div class="flex items-center gap-x-2.5">
          <CreditCard :type="paymentMethod.type" />
          <div
            class="whitespace-nowrap text-[14px] leading-[22px] text-gray-800"
          >
            •••• •••• •••• {{ paymentMethod.last4 }}
          </div>
        </div>
        <button
          type="button"
          class="text-link text-[14px]"
          @click="emit('updatePaymentMethod')"
        >
          Change
        </button>
      </div>
    </div>

    <div v-else :class="cardClasses">
      <div id="card" />
    </div>

    <Alert v-if="errorMessage" variant="error" class="mt-4">
      {{ errorMessage }}
    </Alert>

    <p class="mt-6 text-[12px] font-normal leading-[17px] text-gray-600">
      By clicking Purchase, you authorize Dacr to charge your payment method for
      the amounts shown above and for any usage charges as incurred. Recurring
      fees renew automatically until canceled in account settings. All charges
      are non-refundable except where required by law. Amounts are in
      {{ currency }}.
    </p>

    <div class="mt-4 flex items-center gap-2">
      <Checkbox
        :model-value="acceptTerms"
        :disabled="loading"
        class="shrink-0"
        @update:model-value="emit('update:acceptTerms', $event)"
      />
      <p
        class="cursor-pointer text-[12px] font-normal leading-[17px] text-gray-600"
        @click="emit('update:acceptTerms', !acceptTerms)"
      >
        I agree to the
        <a target="_blank" href="/legal/terms" class="text-link" @click.stop
          >Dacr Terms</a
        >,
        <a target="_blank" href="/legal/privacy" class="text-link" @click.stop
          >Privacy Policy</a
        >, and
        <a target="_blank" href="/legal/payments" class="text-link" @click.stop
          >Payments Policy</a
        >.
      </p>
    </div>

    <Button
      class="mt-5 w-full"
      variant="primary"
      size="lg"
      :disabled="!acceptTerms || awaitingEstimate"
      :loading="loading"
      @click="emit('purchase')"
    >
      Purchase
    </Button>
  </div>
</template>

<script setup lang="ts">
import { Alert, Button, Checkbox } from '@dacr/library'
import { computed } from 'vue'

import CreditCard from '@/features/billing/components/CreditCard.vue'
import type { PlanItem, PlanPriceItem } from '@/shared/graphql/legacy/generated'

const props = defineProps<{
  paymentMethod?: { last4?: string; type: string } | null
  cardClasses: Record<string, boolean>
  acceptTerms: boolean
  loading: boolean
  awaitingEstimate?: boolean
  errorMessage: string
  selectedPlanItem?: PlanItem
  selectedBillingCycle: { type: string; description: string }
  hasMoreThanOnePrice: boolean
  getBillingCyclePrice: (price: PlanPriceItem) => string
}>()

const emit = defineEmits<{
  (e: 'update:acceptTerms', value: boolean): void
  (e: 'updatePaymentMethod'): void
  (e: 'purchase'): void
}>()

const currency = computed(
  () =>
    props.selectedPlanItem?.prices
      .find((price) => price.details?.currency)
      ?.details?.currency?.toUpperCase() || 'USD'
)
</script>
